<template>
    <page title="newCheckingTitle" :textHeight="(WindowSize.y - 276) + 'px'">
        <template #pageContainer>
            <form>
                <v-autocomplete outlined dense
					hide-details="auto"
                    :disabled="loading"
                    :readonly="hasOnlyOnePerformance"
                    v-model="performance.selected"
                    :items="performance.items"
                    required
                    :label="$t('text.performance')"
                    :loading="perfLoading">
                </v-autocomplete>
                <v-autocomplete outlined dense
					hide-details="auto"
                    multiple
                    :disabled="loading || !anyPerfList"
                    :readonly="hasOnlyOnePerfList"
                    v-model="performanceList.selected"
                    :items="performanceList.items"
                    :label="$t('text.performanceList')"
                    :error-messages="performanceListErrors"
                    required
                    :loading="perfListLoading"
                    @input="$v.performanceList.selected.$touch()"
                    @blur="$v.performanceList.selected.$touch()">
                </v-autocomplete>
            </form>
            <spinner :loading="loading"></spinner>
        </template>

		<template #pageActions>
                <v-btn
                    class="mr-4"
                    :disabled="$v.$invalid || loading"
                    @click="create">
                    {{ $t(`text.createButton`) }}
                </v-btn>
                <v-btn
                    :disabled="loading"
                    class="mr-4"
                    @click="back">
                    {{ $t(`text.backButton`) }}
                </v-btn>
		</template>
    </page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import Page from '../general/Page.vue';
import Spinner from '../general/Spinner.vue';

import bestListActions from '../store/actions/bestList';
import perfActions from '../store/actions/performance';
import checkingActions from '../store/actions/checking';
import namespace from '../store/namespace';

import modelState from '../mixins/modelState';

export default {
	name: 'NewChecking',
	mixins: [modelState],
	components: {
		Page,
		Spinner,
	},
	validations: {
		performance: {
			selected: {
				required,
			},
		},
		performanceList: {
			selected: {
				required,
			},
		},
	},
	data() {
		return {
			performance: {
				items: [],
				selected: '',
			},
			performanceList: {
				items: [],
				selected: '',
			},
			perfListLoading: false,
			perfLoading: false,
			loading: false,
			perfId: this.$route.params.perfId,
		};
	},
	computed: {
		...mapGetters(['WindowSize']),
		...mapGetters(namespace.USER, ['User']),
		...mapGetters(namespace.CHECKING, ['Links']),
		performanceListErrors() {
			const errors = [];

			if (!this.$v.performanceList.selected.$dirty) return errors;

			const apiErrors = this.validateProperty('PerformanceListId');

			if (apiErrors.length > 0) {
				errors.push(apiErrors);
			}

			!this.$v.performanceList.selected.required
                && errors.push(this.$i18n.t('text.errorPerformanceListRequired'));

			return errors;
		},
		hasOnlyOnePerfList() {
			return this.performanceList.items.length === 1;
		},
		hasOnlyOnePerformance() {
			return true;
		},
		anyPerfList() {
			return this.performanceList.items.length > 0;
		},
	},
	async mounted() {
		await this.getPerformance();
		await this.getPerformanceList();
	},
	methods: {
		...mapActions(namespace.BESTLIST, [
			bestListActions.GET_BEST_LISTS_FOR_SELECTOR,
		]),
		...mapActions(namespace.PERFORMANCE, [
			perfActions.GET_PERFORMANCE_FOR_SELECTOR,
		]),
		...mapActions(namespace.CHECKING, [
			checkingActions.CREATE_NEW_CHECKING,
		]),
		async create() {
			this.loading = true;
			this.$v.$touch();

			if (!this.$v.$invalid) {
				const link = this.Links.find(x => x.rel === 'new');

				if (link) {
					const newChecking = {
						performanceId: this.performance.selected,
						performanceListIds: this.performanceList.selected,
						userId: this.User.id,
					};

					try {
						this.modelState = {};

						await this.CREATE_NEW_CHECKING({ newChecking, link });

						this.back();
					} catch (e) {
						if (e.response.status === 422) {
							this.modelState = e.response.data;
							if (this.modelError()) {
								throw new Error(this.modelError());
							}
						}

						throw e;
					} finally {
						this.loading = false;
					}
				}
			}
			this.loading = false;
		},
		back() {
			this.$router.go(-1);
		},
		async getPerformance() {
			this.perfLoading = true;
			try {
				this.performance.items = await this.GET_PERFORMANCE_FOR_SELECTOR(
					this.User.masterOrganization.externalId,
				);

				this.performance.selected = this.perfId;
			} finally {
				this.perfLoading = false;
			}
		},
		async getPerformanceList() {
			this.perfListLoading = true;
			try {
				this.performanceList.items = await this.GET_BEST_LISTS_FOR_SELECTOR(
					this.User.masterOrganization.externalId,
				);

				if (this.performanceList.items.length === 1) {
					this.performanceList.selected = this.performanceList.items[0].value;
				}
			} finally {
				this.perfListLoading = false;
			}
		},
	},
};
</script>
